import { Canvas } from '@react-three/fiber';
import { Suspense, lazy, useMemo, useState } from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Background from './components/Background';
import HeaderScene from './components/HeaderScene';
import CarouselStacked from './components/ui/carousel-07';
import ChatBot from './components/ChatBot';
import NetworkScannerSection from './components/NetworkScannerSection';
import SurveyCapabilitiesSection from './components/SurveyCapabilitiesSection';
import MilestonesSection from './components/MilestonesSection';
import AboutSection from './components/AboutSection';
import CoreValuesSection from './components/CoreValuesSection';
import { Footer } from './components/ui/footer-section';

import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import { Html, Box } from '@react-three/drei';
import Preloader from './components/Preloader';
import { motion, AnimatePresence } from 'framer-motion';
import { RegionProvider, useRegion } from './context/RegionContext';
import { Menu, X } from 'lucide-react';

// Fallback component for Scene
const SceneFallback = () => (
  <Box args={[1, 1, 1]}>
    <meshStandardMaterial color="gray" />
  </Box>
);

// Lazy load heavy components with proper fallback
const Scene = lazy(() =>
  import('./components/Scene')
    .catch(error => {
      console.error('Error loading Scene:', error);
      return { default: SceneFallback };
    })
);

const NetworkScannerPage = lazy(() =>
  import('./pages/NetworkScanner')
    .catch(error => {
      console.error('Error loading NetworkScanner:', error);
      return {
        default: () => (
          <div className="p-4 text-center">
            <p className="text-white">Unable to load section</p>
          </div>
        )
      };
    })
);

const CyberSecuritySurveyPage = lazy(() =>
  import('./pages/CyberSecuritySurvey')
    .catch(error => {
      console.error('Error loading CyberSecuritySurvey:', error);
      return {
        default: () => (
          <div className="p-4 text-center">
            <p className="text-white">Unable to load Cybersecurity Survey</p>
          </div>
        )
      };
    })
);

const UKHome = lazy(() => import('./pages/UKHome'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const Internship = lazy(() =>
  import('./pages/Internship')
    .catch(error => {
      console.error('Error loading Internship:', error);
      return {
        default: () => (
          <div className="p-4 text-center">
            <p className="text-white">Unable to load Internship Page. Please check console for details.</p>
          </div>
        )
      };
    })
);
const OurStory = lazy(() => import('./pages/OurStory'));
const Blogs = lazy(() => import('./pages/Blogs'));
const OurServices = lazy(() => import('./pages/OurServices'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ServicePage = lazy(() => import('./pages/ServicePage'));
const MeetOurTeams = lazy(() => import('./pages/MeetOurTeams'));
const MeetTheFounder = lazy(() => import('./pages/MeetTheFounder'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));


// Regular loading fallback for non-Canvas components
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full">
    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Canvas-specific loading fallback
const CanvasLoadingFallback = () => (
  <Html center>
    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
  </Html>
);

// Component to handle region-based routing
const RegionRouter = () => {
  const { region, isLoading } = useRegion();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  console.log(`RegionRouter: Region=${region}, Path=${location.pathname}, Loading=${isLoading}`);

  // Shared canvas configuration (moved here as it's used within this component)
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const canvasConfig = useMemo(() => ({
    gl: {
      antialias: !isMobile, // Disable antialiasing on mobile for better performance
      powerPreference: "high-performance" as const,
      alpha: true,
      stencil: false,
      depth: true,
    },
    dpr: isMobile ? Math.min(window.devicePixelRatio, 2) : window.devicePixelRatio,
  }), [isMobile]);

  if (isLoading) {
    return <Preloader onLoadingComplete={() => { }} />; // Keep preloader while detecting region
  }

  // If user is at root '/' and region is GB, show UKHome (or redirect)
  if (location.pathname === '/' && region === 'GB') {
    console.log("Rendering UKHome");
    return (
      <Suspense fallback={<LoadingFallback />}>
        <UKHome />
      </Suspense>
    );
  }

  return (
    <div className="relative min-h-screen bg-navy">
      <Background />
      {/* Header */}
      <div className="sticky top-4 z-50 px-4 sm:px-6 lg:px-8 w-full mx-auto max-w-7xl">
        <header className="relative border border-amber-500/20 bg-navy/80 backdrop-blur-md rounded-full shadow-lg shadow-amber-500/10">
          <div className="flex items-center justify-between px-4 py-2 sm:px-6 sm:py-3">
          {/* Logo - Left */}
          <div className="flex items-center space-x-1 sm:space-x-3 flex-shrink-0">
            <div className="h-8 w-8 sm:h-12 sm:w-12">
              <Canvas
                {...canvasConfig} // Use canvasConfig here
                shadows
                camera={{ position: [0, 0, 3], fov: 45 }}
                className="!h-full !w-full"
              >
                <ambientLight intensity={0.5} />
                <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} />
                <Suspense fallback={<CanvasLoadingFallback />}>
                  <HeaderScene />
                </Suspense>
              </Canvas>
            </div>
            <span className="text-base sm:text-xl font-bold text-white whitespace-nowrap">Smile Cyber Tech</span>
          </div>

          {/* Centered Desktop Links */}
          <div className="hidden lg:flex items-center justify-center flex-1 mx-4">
            <nav className="flex items-center gap-4 xl:gap-8 text-sm font-medium text-slate-300">
              <Link to="/network-scanner" className="whitespace-nowrap hover:text-[#FFC107] transition-colors">Network Scanner</Link>
              <Link to="/meet-the-founder" className="whitespace-nowrap hover:text-[#FFC107] transition-colors">Meet the Founder</Link>
              <a href="#milestones" onClick={(e) => { e.preventDefault(); document.getElementById('milestones')?.scrollIntoView({ behavior: 'smooth' }); }} className="whitespace-nowrap hover:text-[#FFC107] transition-colors cursor-pointer">Milestones</a>
              <Link to="/our-story" className="whitespace-nowrap hover:text-[#FFC107] transition-colors">Our Foundation</Link>
              <Link to="/security-survey" className="whitespace-nowrap hover:text-[#FFC107] transition-colors">Our Survey</Link>
            </nav>
          </div>

          {/* Right Side: Mobile Menu Toggle */}
          <div className="flex items-center justify-end flex-shrink-0 gap-4">
            <button
              className="lg:hidden flex items-center justify-center p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        </header>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-4 right-4 mt-2 lg:hidden rounded-2xl border border-blue-900/20 bg-navy/95 backdrop-blur-xl overflow-hidden shadow-2xl z-40"
            >
              <nav className="flex flex-col px-4 py-4 gap-4 text-sm font-medium text-slate-300">
                <Link to="/network-scanner" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-white transition-colors">Network Scanner</Link>
                <Link to="/meet-the-founder" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-white transition-colors">Meet the Founder</Link>
                <a href="#milestones" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); document.getElementById('milestones')?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:text-white transition-colors cursor-pointer">Milestones</a>
                <Link to="/our-story" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-white transition-colors">Our Foundation</Link>
                <Link to="/security-survey" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-white transition-colors">Our Survey</Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Hero Section */}
      <main className="relative z-20 w-full bg-[#0B1121] rounded-b-[2rem] sm:rounded-b-[3rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
        <div className="relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-24">
          
          {/* Centered Earth with Rotating Text */}
          <div className="flex flex-col items-center justify-center">
            
            {/* Globe Container with Rotating Text */}
            <div className="relative w-[350px] h-[350px] sm:w-[500px] sm:h-[500px] md:w-[600px] md:h-[600px] lg:w-[750px] lg:h-[750px]">
              
              {/* Rotating Text Ring */}
              <div className="absolute inset-0 z-20 animate-text-reveal pointer-events-none">
                <svg viewBox="0 0 750 750" className="w-full h-full">
                  <defs>
                    <path
                      id="textCircle"
                      d="M 375,45 a 330,330 0 1,1 0,660 a 330,330 0 1,1 0,-660"
                      fill="none"
                    />
                  </defs>
                  <text className="text-[24px] font-bold tracking-[0.15em] uppercase">
                    <textPath href="#textCircle" startOffset="0%">
                      <tspan fill="#ffffff" stroke="#ffffff" strokeWidth="0.5" paintOrder="stroke">{`Pioneering `}</tspan>
                      <tspan fill="#FFC107" stroke="#FFC107" strokeWidth="0.5" paintOrder="stroke">{`GenAI Research `}</tspan>
                      <tspan fill="#ffffff" stroke="#ffffff" strokeWidth="0.5" paintOrder="stroke">&amp; Cybersecurity • </tspan>
                      <tspan fill="#FFC107" stroke="#FFC107" strokeWidth="0.5" paintOrder="stroke">{`Smile Cyber Tech `}</tspan>
                      <tspan fill="#ffffff" stroke="#ffffff" strokeWidth="0.5" paintOrder="stroke">•</tspan>
                    </textPath>
                  </text>
                </svg>
              </div>

              {/* 3D Earth Globe (centered) */}
              <div className="absolute inset-[8%] z-10 rounded-full overflow-hidden">
                <Canvas
                  {...canvasConfig}
                  shadows
                  camera={{ position: [0, 0, 4], fov: 45 }}
                  className="!bg-transparent"
                >
                  <ambientLight intensity={0.5} />
                  <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} />
                  <Suspense fallback={<CanvasLoadingFallback />}>
                    <Scene />
                  </Suspense>
                </Canvas>
              </div>
            </div>

            {/* Subtitle */}
            <p className="text-slate-300 text-base sm:text-lg md:text-xl max-w-2xl mb-10 leading-relaxed font-light text-center mt-8">
              An advanced research & engineering lab building production-grade Generative AI models, autonomous multi-agent systems, and enterprise cyber defense.
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4 mb-12 sm:mb-16">
              <Link to="/our-services" className="px-6 sm:px-8 py-3 sm:py-3.5 bg-[#FFC107] text-[#0B1121] font-bold rounded-full hover:bg-[#FFD54F] transition-colors shadow-[0_0_20px_rgba(255,193,7,0.3)] whitespace-nowrap">
                Explore Services
              </Link>
              <Link to="/our-story" className="px-6 sm:px-8 py-3 sm:py-3.5 bg-transparent border border-slate-600 text-white font-medium rounded-full hover:bg-white/5 transition-colors whitespace-nowrap">
                Our Journey
              </Link>
            </div>

            {/* Metrics */}
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 xl:gap-12">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white mb-1">5+</div>
                <div className="text-[10px] sm:text-xs text-slate-400 font-bold tracking-widest uppercase">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white mb-1">50+</div>
                <div className="text-[10px] sm:text-xs text-slate-400 font-bold tracking-widest uppercase">Projects Shipped</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white mb-1">100%</div>
                <div className="text-[10px] sm:text-xs text-slate-400 font-bold tracking-widest uppercase">Enterprise Security</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white mb-1">24/7</div>
                <div className="text-[10px] sm:text-xs text-slate-400 font-bold tracking-widest uppercase">Threat Defense</div>
              </div>
            </div>
          </div>
        </div>
      </div>
          
          {/* Regions Carousel Section */}
          <div className="relative w-full overflow-hidden mt-12 mb-24 z-10">
            <CarouselStacked />
          </div>

          {/* Milestones Section */}
          <div className="w-full">
            <MilestonesSection />
          </div>

          {/* About Section */}
          <div className="w-full">
            <AboutSection />
          </div>

          {/* Core Values Section */}
          <div className="w-full">
            <CoreValuesSection />
          </div>

          {/* Cybersecurity Survey Section */}
          <div className="w-full">
            <SurveyCapabilitiesSection />
          </div>

          {/* Network Scanner Section */}
          <div className="w-full">
            <NetworkScannerSection />
          </div>
        </main>

      {/* Animated Footer */}
      <Footer />
    </div>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true); // Keep isLoading for initial app preloader

  return (
    <HelmetProvider>
      <RegionProvider>
        <Helmet>
          <meta name="description" content="Smile Cyber Tech – A GenAI-powered cybersecurity company dedicated to protecting students, families, and institutions. We offer AI-driven cyber safety solutions, online security training, and advanced protection against cyber threats. Stay safe online with Smile Cyber Tech." />
        </Helmet>
        <Router>
          <AnimatePresence mode="wait">
            {isLoading ? (
              <Preloader onLoadingComplete={() => setIsLoading(false)} />
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Routes>
                  <Route path="/" element={<RegionRouter />} />
                  <Route
                    path="/network-scanner"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <NetworkScannerPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/security-survey"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <CyberSecuritySurveyPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/contact"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <ContactUs />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/internship"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <Internship />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/our-story"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <OurStory />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/blogs"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <Blogs />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/team"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <MeetOurTeams />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/meet-the-founder"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <MeetTheFounder />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/blogs/:slug"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <Blogs />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/our-services"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <OurServices />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/privacy-policy"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <PrivacyPolicyPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/terms"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <TermsOfServicePage />
                      </Suspense>
                    }
                  />

                  <Route
                    path="/admin"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminDashboard />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/services/:slug"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <ServicePage />
                      </Suspense>
                    }
                  />



                </Routes>
                <ChatBot />
              </motion.div>
            )}
          </AnimatePresence>
        </Router>
      </RegionProvider>
    </HelmetProvider>
  );
}

export default App;

